import { useEffect, useState, type RefObject } from 'react'
import { useAuth } from '@/hooks/use-auth'
import { preferences, useStore } from '@/lib/store'

/** What is actually applied: guests are always in safe mode. */
export function useSafeMode() {
  const { isLoggedIn } = useAuth()
  const prefs = useStore(preferences)
  const safe = !isLoggedIn || prefs.safeMode
  return {
    safe,
    canToggle: isLoggedIn,
    toggle: () => {
      if (isLoggedIn) preferences.set((p) => ({ ...p, safeMode: !p.safeMode }))
    },
  }
}

/** Current time, refreshed every `intervalMs`, for relative timestamps. */
export function useNow(intervalMs = 15_000): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs)
    return () => clearInterval(t)
  }, [intervalMs])
  return now
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches)
  useEffect(() => {
    const mq = window.matchMedia(query)
    const onChange = () => setMatches(mq.matches)
    onChange()
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [query])
  return matches
}

export function useElementWidth(ref: RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => setWidth(Math.round(entry.contentRect.width)))
    ro.observe(el)
    setWidth(el.clientWidth)
    return () => ro.disconnect()
  }, [ref])
  return width
}
